import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { string } from 'prop-types';
import { createLoadingSelector } from 'erisxkit/client';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import cogoToast from 'cogo-toast';

import XTable7 from '../../common/table/XTable7';
import {
  fetchRiskConfigurations,
  createRiskConfigPromise,
  updateRiskConfigPromise,
  deleteRiskConfigPromise,
  getRiskConfigSectionCount,
  getRiskConfigSectionData,
  RISK_CONFIGURATIONS,
} from '../../reducers/riskConfigurationsReducer';
import objectDiff from '../../utils/objectDiff';
import usePaginatedFetch from '../../hooks/usePaginatedFetch';

const RiskConfigTable = ({
  configSection,
  metadata,
  editable,
  hideActions,
  isAddNewDisabled,
  template,
  fetchAction,
  fetchParams,
  onSaveOverride,
  selectorData,
  selectorCount,
  loadingKey,
}) => {
  const dispatch = useDispatch();

  // Use custom loading key if provided, otherwise default to RISK_CONFIGURATIONS
  const loading = useSelector(
    createLoadingSelector([loadingKey || RISK_CONFIGURATIONS]),
  );

  // Use custom selectors if provided, otherwise use default risk config selectors
  const data = useSelector((state) =>
    selectorData
      ? selectorData(state)
      : getRiskConfigSectionData(state, configSection),
  );
  const count = useSelector((state) =>
    selectorCount
      ? selectorCount(state)
      : getRiskConfigSectionCount(state, configSection),
  );

  // Use custom fetch action and params if provided
  const fetchReports = usePaginatedFetch(
    fetchAction || fetchRiskConfigurations,
    fetchParams !== undefined ? fetchParams : { configSection },
  );

  const onApply = useCallback(
    async (changes) => {
      try {
        const { create, remove, update } = changes;
        if (!isEmpty(create)) {
          await createRiskConfigPromise(
            { configSection, items: create },
            dispatch,
          );
        }
        if (!isEmpty(update)) {
          // For updates, we only send the keys to update with their new value, not the whole object
          const items = update.map((change) =>
            objectDiff(change?.original, change?.updated, ['uuid']),
          );
          await updateRiskConfigPromise({ configSection, items }, dispatch);
        }
        if (!isEmpty(remove)) {
          // For removals we only send the uuids
          const items = remove.map((r) => ({ uuid: get(r, 'uuid', '') }));
          await deleteRiskConfigPromise({ configSection, items }, dispatch);
        }
      } catch (e) {
        cogoToast.error('Could not apply changes');
      }
    },
    [configSection, dispatch],
  );

  return (
    <XTable7
      title={configSection}
      metadata={metadata}
      data={data}
      count={count}
      loading={loading}
      fetchData={fetchReports}
      editable={editable}
      onSaveChanges={onSaveOverride || onApply}
      editActionsToHide={hideActions}
      isAddNewButtonDisabled={isAddNewDisabled}
      newRowTemplate={template}
    />
  );
};

RiskConfigTable.defaultProps = {
  configSection: '',
  metadata: [],
  editable: false,
  hideActions: [],
  isAddNewDisabled: undefined,
  template: {},
  fetchAction: undefined,
  fetchParams: undefined,
  onSaveOverride: undefined,
  selectorData: undefined,
  selectorCount: undefined,
  loadingKey: undefined,
};

RiskConfigTable.propTypes = {
  configSection: PropTypes.string,
  metadata: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
      Header: PropTypes.string.isRequired,
    }),
  ),
  editable: PropTypes.bool,
  hideActions: PropTypes.arrayOf(PropTypes.string),
  isAddNewDisabled: PropTypes.func,
  template: PropTypes.objectOf(PropTypes.any),
  fetchAction: PropTypes.func,
  fetchParams: PropTypes.objectOf(PropTypes.any),
  onSaveOverride: PropTypes.func,
  selectorData: PropTypes.func,
  selectorCount: PropTypes.func,
  loadingKey: PropTypes.string,
};

export default RiskConfigTable;
